import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import DeskIcon from "@mui/icons-material/Desk";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useSaveUser } from "common/hooks/gqlHooks";
import { useAuth } from "hooks/useAuth";
import { setGlobalMessage } from "hooks/useGlobalMessage";
import { RegisterUserData, RegisterUserDataSchema } from "./schema";

export default function RegisterUserDataForm() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { saveUser } = useSaveUser();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterUserData>({
    resolver: zodResolver(RegisterUserDataSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      companyName: "",
      departmentName: "",
      divisionName: undefined,
    },
  });

  const onSubmit = async (data: RegisterUserData) => {
    const { data: result, error } = await saveUser({ data: {
      last_name: data.lastName,
      first_name: data.firstName,
      company_name: data.companyName,
      department_name: data.departmentName,
      division_name: data.divisionName,
    }});
    if (error) {
      setGlobalMessage({
        message: "ユーザー情報の保存に失敗しました。",
        severity: "error",
      });
      return;
    } else {
      setGlobalMessage({
        message: "ユーザー情報を保存しました。",
        severity: "success",
      });
      setUser(result?.saveUser ?? null);
      navigate("/");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box>
        <FormControl
          fullWidth
          margin="normal"
        >
          <Stack direction="row" spacing={2}>
            <TextField
              {...register("lastName")}
              type="text"
              variant="outlined"
              fullWidth
              label="姓"
              required
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              {...register("firstName")}
              type="text"
              variant="outlined"
              fullWidth
              label="名"
              required
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </FormControl>
        <FormControl
          fullWidth
          margin="normal"
        >
          <TextField
            {...register("companyName")}
            type="text"
            variant="outlined"
            fullWidth
            label="企業名"
            required
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <FormControl
          fullWidth
          margin="normal"
        >
          <TextField
            {...register("departmentName")}
            type="text"
            variant="outlined"
            fullWidth
            label="部署名"
            required
            error={!!errors.departmentName}
            helperText={errors.departmentName?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DeskIcon />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <FormControl
          fullWidth
          margin="normal"
        >
          <TextField
            {...register("divisionName")}
            type="text"
            variant="outlined"
            fullWidth
            label="部門名（課、グループなど）"
            error={!!errors.divisionName}
            helperText={errors.divisionName?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DeskIcon />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 3, mb: 2 }}
          disabled={isSubmitting}
        >
          送信
        </Button>
      </Box>
    </form>
  )
}
